import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import EditableImage from "../../landingEditor/EditableImage";
import { isSvgLandingAsset } from "../../../../utils/landingAssets";
import { getValueAtDescriptorPath } from "../elementEditing";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { VisualColorControl } from "../inspector/VisualSettingControls";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";

// WP-STANDARD | NAVBAR CONTEXTUAL GUIDE ANCHORS
// STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_RUNTIME_PENDING
// Tutorial anchors only wrap existing image controls and do not own upload state.

function colorInputValue(value) {
  const candidate = String(value || "").trim();
  return /^#[0-9a-f]{6}$/i.test(candidate) ? candidate : "#000000";
}

function resolvedAppearanceAsset(form, paths = []) {
  for (const path of paths) {
    const value = getValueAtDescriptorPath(form, path, "");
    if (String(value || "").trim()) return value;
  }
  return "";
}

function LogoAppearanceGroup({ group, form, disabled, onCommit }) {
  const asset = resolvedAppearanceAsset(form, group?.assetPaths || []);
  const svg = isSvgLandingAsset(asset);
  const color = String(getValueAtDescriptorPath(form, group?.colorPath, "") || "").trim();
  const rawSize = getValueAtDescriptorPath(form, group?.sizePath, "");
  const hasSize = rawSize !== "" && rawSize !== null && rawSize !== undefined && Number.isFinite(Number(rawSize));
  const min = Number(group?.min ?? 20);
  const max = Number(group?.max ?? 160);
  const step = Number(group?.step ?? 1);
  const surfacePrefix = String(group?.surfacePrefix || "");
  const surfaceBackground = surfacePrefix ? String(getValueAtDescriptorPath(form, `${surfacePrefix}backgroundColor`, "transparent") || "transparent") : "";
  const surfaceBorder = surfacePrefix ? String(getValueAtDescriptorPath(form, `${surfacePrefix}borderColor`, "transparent") || "transparent") : "";
  const surfaceShadow = surfacePrefix ? String(getValueAtDescriptorPath(form, `${surfacePrefix}boxShadow`, "none") || "none") : "";

  if (!asset) {
    return (
      <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)", border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography variant="body2" sx={{ fontWeight: 850 }}>{group?.label || "Logo"}</Typography>
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
          Agregá un logo para ajustar su apariencia en esta ubicación.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.1} sx={{ p: 1.25, borderRadius: 2, bgcolor: "rgba(25,36,47,.035)", border: "1px solid rgba(25,36,47,.08)" }}>
      <Typography variant="body2" sx={{ fontWeight: 900 }}>{group?.label || "Logo"}</Typography>

      {svg ? (
        <Stack spacing={0.65}>
          <Typography variant="caption" sx={{ fontWeight: 850, color: "rgba(25,36,47,.72)" }}>
            Color del SVG
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              component="input"
              type="color"
              aria-label={`Color del SVG · ${group?.label || "logo"}`}
              value={colorInputValue(color)}
              disabled={disabled}
              onChange={(event) => onCommit?.(group.colorPath, event.target.value)}
              sx={{ width: 48, height: 40, p: 0.2, border: "1px solid rgba(25,36,47,.14)", borderRadius: 1.5, bgcolor: "transparent", cursor: disabled ? "default" : "pointer" }}
            />
            <Typography variant="caption" sx={{ flex: 1, minWidth: 0, color: "rgba(25,36,47,.62)" }}>
              {color || "Colores originales"}
            </Typography>
            {color ? (
              <Button size="small" disabled={disabled} onClick={() => onCommit?.(group.colorPath, "")} sx={{ textTransform: "none", fontWeight: 850 }}>
                Usar colores originales
              </Button>
            ) : null}
          </Stack>
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.62)" }}>
          El cambio de color está disponible para logos SVG.
        </Typography>
      )}

      {surfacePrefix ? (
        <Stack spacing={0.8}>
          <Typography variant="caption" sx={{ fontWeight: 850, color: "rgba(25,36,47,.72)" }}>Superficie propia del logo</Typography>
          <VisualColorControl
            label="Fondo"
            value={surfaceBackground}
            fallback="transparent"
            disabled={disabled}
            allowExactCss
            helperText="Afecta sólo este logo."
            onChange={(next) => onCommit?.(`${surfacePrefix}backgroundColor`, next)}
            onReset={() => onCommit?.(`${surfacePrefix}backgroundColor`, "transparent")}
          />
          <VisualColorControl
            label="Borde"
            value={surfaceBorder}
            fallback="transparent"
            disabled={disabled}
            allowExactCss
            helperText="Afecta sólo este logo."
            onChange={(next) => onCommit?.(`${surfacePrefix}borderColor`, next)}
            onReset={() => onCommit?.(`${surfacePrefix}borderColor`, "transparent")}
          />
          <TextField
            size="small"
            label="Sombra CSS"
            value={surfaceShadow}
            disabled={disabled}
            inputProps={{ maxLength: 300 }}
            onChange={(event) => onCommit?.(`${surfacePrefix}boxShadow`, event.target.value)}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
            {[
              ["paddingX", "Padding X", 64],
              ["paddingY", "Padding Y", 48],
              ["borderRadius", "Redondeado", 160],
            ].map(([key, label, maxValue]) => (
              <TextField
                key={key}
                size="small"
                type="number"
                label={label}
                value={getValueAtDescriptorPath(form, `${surfacePrefix}${key}`, 0) ?? 0}
                disabled={disabled}
                inputProps={{ min: 0, max: maxValue, step: 0.5 }}
                onChange={(event) => onCommit?.(`${surfacePrefix}${key}`, Math.min(maxValue, Math.max(0, Number(event.target.value) || 0)))}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}

      <Stack spacing={0.65}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" sx={{ fontWeight: 850, color: "rgba(25,36,47,.72)" }}>
            Tamaño
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 750, whiteSpace: "nowrap" }}>
              {hasSize ? "Personalizado" : "Automático"}
            </Typography>
            {hasSize ? (
              <Button size="small" disabled={disabled} onClick={() => onCommit?.(group.sizePath, "")} sx={{ minWidth: 0, px: 0.75, textTransform: "none", fontWeight: 850 }}>
                Automático
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Slider
          min={min}
          max={max}
          step={step}
          value={hasSize ? Math.min(max, Math.max(min, Number(rawSize))) : min}
          track={hasSize ? "normal" : false}
          disabled={disabled}
          valueLabelDisplay="off"
          aria-label={`Tamaño del logo · ${group?.label || "logo"}`}
          getAriaValueText={() => (hasSize ? "Tamaño personalizado" : "Tamaño automático")}
          onChange={(_, nextValue) => {
            const next = Number(nextValue);
            if (!Number.isFinite(next)) return;
            onCommit?.(group.sizePath, Math.min(max, Math.max(min, next)));
          }}
          onChangeCommitted={(_, nextValue) => {
            if (hasSize) return;
            const next = Number(nextValue);
            if (!Number.isFinite(next)) return;
            onCommit?.(group.sizePath, Math.min(max, Math.max(min, next)));
          }}
          sx={{
            mx: 0.25,
            width: "calc(100% - 4px)",
            "& .MuiSlider-thumb": { width: 20, height: 20 },
          }}
        />

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.54)" }}>Más chico</Typography>
          <Typography variant="caption" sx={{ color: "rgba(25,36,47,.54)" }}>Más grande</Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)", lineHeight: 1.35 }}>
          {hasSize
            ? "Arrastrá la barra para ajustar el logo."
            : "Automático conserva el tamaño responsive. Mové la barra para personalizarlo."}
        </Typography>
      </Stack>
    </Stack>
  );
}

function ImageStyleFields({ fields = [], form, disabled, onCommit, sectionType }) {
  if (!fields.length) return null;
  return (
    <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia de la imagen</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {fields.map((field) => {
            const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
            if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
              return (
                <VisualColorControl
                  key={field.path}
                  label={field.label}
                  value={String(value ?? "")}
                  fallback={String(field.defaultValue || "transparent")}
                  disabled={disabled}
                  allowExactCss
                  helperText="Valor visual explícito de esta imagen."
                  onChange={(next) => onCommit?.(field.path, next)}
                  onReset={() => onCommit?.(field.path, field.defaultValue ?? "transparent")}
                />
              );
            }
            if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
              return (
                <TextField
                  key={field.path}
                  fullWidth
                  size="small"
                  type="number"
                  label={field.label}
                  value={value ?? ""}
                  disabled={disabled}
                  inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }}
                  onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))}
                />
              );
            }
            if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
              return (
                <TextField key={field.path} fullWidth select SelectProps={{ native: true }} size="small" label={field.label} value={value ?? field.defaultValue ?? ""} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.value)}>
                  {(field.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </TextField>
              );
            }
            if (field.type === SEGMENT_FIELD_TYPES.TEXT && ["imageCarousel", "testimonials"].includes(String(sectionType || ""))) {
              return <TextField key={field.path} fullWidth size="small" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ maxLength: field.maxLength || 300 }} helperText="CSS exacto de sombra." onChange={(event) => onCommit?.(field.path, event.target.value)} />;
            }
            return null;
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function ImageElementEditor({ descriptor, form, baselineForm, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const field = descriptor?.field;
  const path = descriptor?.fieldPath || field?.path || "";
  if (!field || !path) return null;

  const value = getValueAtDescriptorPath(form, path, "");
  const altField = descriptor?.altField;
  const altPath = altField?.path || field?.altPath || "";
  const positionPath = field?.positionPath || "";
  const positionOptions = field?.positionOptions || [];
  const mobileField = descriptor?.mobileVariantField;
  const showField = descriptor?.showField;
  const appearanceGroups = Array.isArray(descriptor?.logoAppearance?.groups)
    ? descriptor.logoAppearance.groups
    : [];
  const styleFields = Array.isArray(descriptor?.styleFields) ? descriptor.styleFields : [];

  return (
    <Stack spacing={1.4}>
      {showField ? (
        <FormControlLabel
          control={<Switch checked={getValueAtDescriptorPath(form, showField.path, true) !== false} disabled={disabled} onChange={(event) => onCommit?.(showField.path, event.target.checked)} />}
          label={showField.label || "Mostrar imagen"}
        />
      ) : null}

      <Box data-wp-navbar-guide-target="image-main">
        <EditableImage
          value={value}
          label={field.label || descriptor.label || "Imagen"}
          disabled={disabled}
          onUpload={(file, metadata) => onUploadImage?.(path, file, metadata)}
          onRemove={() => onRemoveImage?.(path)}
        />
      </Box>

      {altPath ? (
        <LandingCopyTextField
          key={`${altPath}:${String(getValueAtDescriptorPath(form, altPath, ""))}`}
          fullWidth
          size="small"
          label={altField?.label || "Texto alternativo"}
          field={{ ...(altField || {}), path: altPath, maxLength: altField?.maxLength || 220 }}
          path={altPath}
          form={form}
          baselineForm={baselineForm}
          value={getValueAtDescriptorPath(form, altPath, "")}
          disabled={disabled}
          helperText="Describe la imagen para accesibilidad."
          onCommit={(nextValue) => onCommit?.(altPath, nextValue)}
        />
      ) : null}

      {positionPath ? (
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel>{field.positionLabel || "Encuadre"}</InputLabel>
          <Select
            label={field.positionLabel || "Encuadre"}
            value={getValueAtDescriptorPath(form, positionPath, field.positionDefaultValue || "center") || "center"}
            onChange={(event) => onCommit?.(positionPath, event.target.value)}
          >
            {(positionOptions.length ? positionOptions : [
              { value: "center", label: "Centro" },
              { value: "top", label: "Arriba" },
              { value: "bottom", label: "Abajo" },
              { value: "left", label: "Izquierda" },
              { value: "right", label: "Derecha" },
            ]).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </Select>
        </FormControl>
      ) : null}

      {mobileField ? (
        <Box data-wp-navbar-guide-target="image-mobile">
          <EditableImage
            value={getValueAtDescriptorPath(form, mobileField.path, "")}
            label={mobileField.label || "Variante móvil"}
            disabled={disabled}
            onUpload={(file, metadata) => onUploadImage?.(mobileField.path, file, metadata)}
            onRemove={() => onRemoveImage?.(mobileField.path)}
          />
        </Box>
      ) : null}

      <ImageStyleFields fields={styleFields} form={form} disabled={disabled} onCommit={onCommit} sectionType={descriptor?.sectionType} />

      {appearanceGroups.length ? (
        <Stack data-wp-navbar-guide-target="logo-appearance" spacing={1.05} sx={{ pt: 0.4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 950, color: "#19242F" }}>
            Apariencia del logo
          </Typography>
          {appearanceGroups.map((group) => (
            <LogoAppearanceGroup
              key={`${group.colorPath}:${group.sizePath}`}
              group={group}
              form={form}
              disabled={disabled}
              onCommit={onCommit}
            />
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}
